import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {
  connectionMutationKey,
  type ConnectionMutationVariables,
} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {createSubIssueFromChecklistItemMutation as createSubIssueFromChecklistItemQueryId} from '@github-ui/server-persisted-queries'
import type {CreateSubIssueFromChecklistItemMutationMutation} from '@github-ui/server-persisted-queries/types/createSubIssueFromChecklistItemMutation'
import {isSubIssuesOptimisticOverlayEnabled} from '@github-ui/sub-issues/optimistic-overlay'
import {
  buildSubIssueNodeFromServer,
  buildSubIssueOptimisticNode,
  createTempSubIssueId,
} from '@github-ui/sub-issues/sub-issue-optimistic-node'
import {useCallback} from 'react'

import type {ChecklistItemConversionRoute} from './checklist-item-conversion-helpers'
import {postChecklistItemConversion, settleIssueFields, throwPayloadErrors} from './checklist-item-conversion-helpers'

export type CreateSubIssueFromChecklistItemVariables = ChecklistItemConversionRoute & {
  body: string
  position: readonly number[]
  title: string
}

type CreateSubIssueMutationVariables = CreateSubIssueFromChecklistItemVariables & ConnectionMutationVariables

type Issue = NonNullable<
  NonNullable<CreateSubIssueFromChecklistItemMutationMutation['convertChecklistItemToSubIssue']>['issue']
>
type ParentIssue = NonNullable<Issue['parent']>
type SuccessfulIssue = Omit<Issue, 'parent'> & {parent: ParentIssue}
type ConversionResult = {
  data: CreateSubIssueFromChecklistItemMutationMutation
  issue: SuccessfulIssue
}

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']

function buildAddOperation(
  node: ReturnType<typeof buildSubIssueOptimisticNode> & {[OPTIMISTIC_KEY]?: string},
): ConnectionOperation {
  return {
    type: 'addToConnection',
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    node,
    anchor: {at: 'end'},
    counts: [
      {path: ['subIssuesSummary', 'total'], delta: 1},
      {path: ['subIssuesConnection', 'totalCount'], delta: 1},
      ...(node.closed ? [{path: ['subIssuesSummary', 'completed'], delta: 1}] : []),
    ],
  }
}

function buildOptimisticAddOperation(title: string): ConnectionOperation {
  const id = createTempSubIssueId()
  return buildAddOperation(buildSubIssueOptimisticNode({id, title}))
}

function confirmedParentFields(issue: SuccessfulIssue) {
  const {parent} = issue
  const nodes = parent.subIssues.nodes ?? []
  const subIssuesSummary = {
    total: parent.subIssuesConnection.totalCount,
    completed: nodes.reduce((total, node) => total + (node?.closed ? 1 : 0), 0),
  }

  return {subIssuesSummary}
}

function subIssueFromResponse(data: CreateSubIssueFromChecklistItemMutationMutation): SuccessfulIssue {
  const payload = data.convertChecklistItemToSubIssue
  if (!payload) throw new Error('Create sub-issue from checklist item returned no payload')
  throwPayloadErrors(payload.errors, 'convertChecklistItemToSubIssue')

  const issue = payload.issue
  if (!issue) throw new Error('Create sub-issue from checklist item returned no issue')
  if (!issue.parent) throw new Error('Create sub-issue from checklist item returned no parent issue')

  return {...issue, parent: issue.parent}
}

export function useCreateSubIssueFromChecklistItemMutation(parentIssueId: string) {
  const mutation = useMutation<ConversionResult, Error, CreateSubIssueMutationVariables>({
    mutationKey: connectionMutationKey(parentIssueId, SUB_ISSUES_CONNECTION_PATH),
    mutationFn: async ({owner, repository, issueNumber, body, position}) => {
      const input = {body, position: [...position]}
      const data = await postChecklistItemConversion<CreateSubIssueFromChecklistItemMutationMutation, typeof input>(
        {owner, repository, issueNumber},
        'checklist_item_sub_issues',
        input,
        createSubIssueFromChecklistItemQueryId,
      )

      return {data, issue: subIssueFromResponse(data)}
    },
    onSuccess: async ({issue}, {operation}) => {
      if (operation.type !== 'addToConnection') throw new Error('Invalid checklist sub-issue operation')
      const {subIssuesSummary} = confirmedParentFields(issue)
      const {id: parentId, body: parentBody, bodyHTML, subIssues, subIssuesConnection} = issue.parent
      const {id: issueId, ...issueFields} = issue
      const optimisticOverlayEnabled = isSubIssuesOptimisticOverlayEnabled()

      await settleIssueFields({
        type: 'setFields',
        id: parentId,
        fields: optimisticOverlayEnabled
          ? {body: parentBody, bodyHTML}
          : {body: parentBody, bodyHTML, subIssues, subIssuesConnection, subIssuesSummary},
      })
      if (optimisticOverlayEnabled) {
        await notifySettlement(
          buildAddOperation({...buildSubIssueNodeFromServer(issue), [OPTIMISTIC_KEY]: operation.node.id}),
          parentId,
          SUB_ISSUES_CONNECTION_PATH,
        )
      }
      await settleIssueFields({
        type: 'setFields',
        id: issueId,
        fields: issueFields,
      })
    },
  })

  const {mutateAsync} = mutation
  const convert = useCallback(
    (variables: CreateSubIssueFromChecklistItemVariables) =>
      mutateAsync({...variables, operation: buildOptimisticAddOperation(variables.title)}),
    [mutateAsync],
  )

  return {...mutation, mutateAsync: convert}
}
